import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { initReduxStore } from './app/InitReduxStore';
import { getAppStore } from './app/AppStore';
import { ConnectedAppRouter } from './app/components/Router';
import {
  SearchMicroViewRenderChannel,
  SearchMicroViewDisposeChannel,
  SearchMicroViewBundleReadyChannel,
} from '@tableau/search-microview-communication-channels';
import { startMicroview } from '@tableau/microview';
import { searchMetadata } from '@tableau/search-microview-communication-channels/lib/src/metadata/Metadata';
import { FeatureFlag, FeatureFlagService } from './search/services/FeatureFlagService';

export type SearchMicroViewOwnProps = {};

export interface SearchMicroViewDispatchProps {
  readonly enterSpace: (spaceInfo: unknown) => void;
}

export type SearchMicroViewProps = SearchMicroViewOwnProps & SearchMicroViewDispatchProps;

initReduxStore();

FeatureFlagService.instance.getValue(FeatureFlag.genericMicroview)
  ? startMicroview<SearchMicroViewProps>(searchMetadata, SearchWithGenericMicroviewPackage)
  : startSearch();

function SearchWithGenericMicroviewPackage(): JSX.Element {
  return (
    <React.Fragment>
      <Provider store={getAppStore()}>
        <ConnectedAppRouter />
      </Provider>
    </React.Fragment>
  );
}

export const SearchMicroViewHostId = 'SearchMicroViewHost';
function startSearch() {
  new SearchMicroViewRenderChannel().listen(() => {
    initialize();
  });

  new SearchMicroViewDisposeChannel().listen(() => {
    const hostElement = getMicroViewHostElement();
    if (hostElement) {
      // We reluctantly take this as a temporary fix.
      // The following story will track work to remove this entirely. Bottom line, we have a leak and need to hunt it down.
      // Story 1347295: [FE] [Techdebt] The microview must unload cleanly when unmounted from the dom
      ReactDOM.unmountComponentAtNode(hostElement);
    }
  });

  new SearchMicroViewBundleReadyChannel().call(); // It tells the microview host the microview bundle code is ready to work.

  function initialize() {
    if (getMicroViewHostElement()) {
      waitForLocalizationJSDownloaded().then(() => {
        render();
      });
    } else {
      window.onload = function () {
        waitForLocalizationJSDownloaded().then(() => {
          render();
        });
      };
    }
  }

  function render() {
    const hostElement = getMicroViewHostElement();
    if (hostElement) {
      ReactDOM.render(
        <React.Fragment>
          <Provider store={getAppStore()}>
            <ConnectedAppRouter />
          </Provider>
        </React.Fragment>,
        hostElement
      );
    }
  }

  function getMicroViewHostElement() {
    return document.getElementById(SearchMicroViewHostId);
  }

  // The following function is a temporary implementation for sync up with localization JS initialization.
  // After communication service is implemented, the following function will be removed.
  // Instead, we will use communication service to tell the micro view that the localization JS initialization is done.
  function waitForLocalizationJSDownloaded(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let retryCount = 0;
      const handler = () => {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        if (!(window as any)['LocalizeNamespace-search-microview']?.Localize?.msg?.formatMessage) {
          if (retryCount > 30) {
            reject('Exceeds the max amount of retry attempt to wait for the localization JS to be downloaded.');
          }
          retryCount++;
          setTimeout(() => {
            handler();
          }, 100);
        } else {
          resolve();
        }
      };
      handler();
    });
  }
}
